export function BarChart({ data, height = 200, maxValue = null }) {
  const max = maxValue || Math.max(...data.map(d => d.value), 1)

  return (
    <div className="space-y-3">
      {data.map((item, index) => {
        const percentage = (item.value / max) * 100

        return (
          <div key={index} className="flex items-center gap-4">
            <div className="w-32 text-sm text-secondary-700 truncate" title={item.label}>
              {item.label}
            </div>
            <div className="flex-1">
              <div className="relative h-8 bg-secondary-100 rounded">
                <div
                  className="absolute inset-y-0 left-0 bg-primary-600 rounded transition-all duration-500"
                  style={{ width: `${percentage}%` }}
                >
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-white">
                    {item.value}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function LineChart({ data, height = 200 }) {
  if (!data || data.length === 0) {
    return <div className="text-secondary-500 text-sm">No data available</div>
  }

  const max = Math.max(...data.map(d => d.value), 1)
  const min = Math.min(...data.map(d => d.value), 0)
  const range = max - min || 1

  const points = data.map((item, index) => {
    const x = (index / (data.length - 1)) * 100
    const y = 100 - ((item.value - min) / range) * 100
    return `${x},${y}`
  }).join(' ')

  return (
    <div className="space-y-4">
      <svg
        className="w-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ height: `${height}px` }}
      >
        <polyline
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-primary-600"
          points={points}
        />
        {data.map((item, index) => {
          const x = (index / (data.length - 1)) * 100
          const y = 100 - ((item.value - min) / range) * 100
          return (
            <circle
              key={index}
              cx={x}
              cy={y}
              r="2"
              className="fill-primary-600"
            />
          )
        })}
      </svg>
      <div className="flex justify-between text-xs text-secondary-600">
        {data.map((item, index) => (
          <div key={index} className="text-center">
            <div>{item.label}</div>
            <div className="font-semibold text-secondary-900">{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function PieChart({ data, size = 200 }) {
  const total = data.reduce((sum, item) => sum + item.value, 0)
  if (total === 0) {
    return <div className="text-secondary-500 text-sm">No data available</div>
  }

  let currentAngle = 0
  const segments = data.map(item => {
    const percentage = (item.value / total) * 100
    const angle = (item.value / total) * 360
    const startAngle = currentAngle
    currentAngle += angle

    return {
      ...item,
      percentage,
      startAngle,
      endAngle: currentAngle,
    }
  })

  const colors = [
    'bg-primary-600',
    'bg-blue-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-orange-500',
    'bg-red-500',
    'bg-purple-500',
    'bg-pink-500',
  ]

  return (
    <div className="flex items-center gap-8">
      <div
        className="relative rounded-full overflow-hidden bg-secondary-100"
        style={{ width: size, height: size }}
      >
        {segments.map((segment, index) => {
          const startAngle = (segment.startAngle - 90) * (Math.PI / 180)
          const endAngle = (segment.endAngle - 90) * (Math.PI / 180)

          const x1 = size / 2 + (size / 2) * Math.cos(startAngle)
          const y1 = size / 2 + (size / 2) * Math.sin(startAngle)
          const x2 = size / 2 + (size / 2) * Math.cos(endAngle)
          const y2 = size / 2 + (size / 2) * Math.sin(endAngle)

          const largeArc = segment.endAngle - segment.startAngle > 180 ? 1 : 0

          const pathData = [
            `M ${size / 2} ${size / 2}`,
            `L ${x1} ${y1}`,
            `A ${size / 2} ${size / 2} 0 ${largeArc} 1 ${x2} ${y2}`,
            'Z',
          ].join(' ')

          return (
            <svg
              key={index}
              className="absolute inset-0"
              viewBox={`0 0 ${size} ${size}`}
            >
              <path
                d={pathData}
                className={colors[index % colors.length].replace('bg-', 'fill-')}
                opacity="0.8"
              />
            </svg>
          )
        })}
      </div>

      <div className="space-y-2">
        {segments.map((segment, index) => (
          <div key={index} className="flex items-center gap-2">
            <div className={`w-4 h-4 rounded ${colors[index % colors.length]}`} />
            <div className="text-sm">
              <span className="text-secondary-900 font-medium">{segment.label}</span>
              <span className="text-secondary-600 ml-2">
                {segment.percentage.toFixed(1)}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function StatCard({ title, value, change, icon, trend = 'up' }) {
  const trendColor = trend === 'up' ? 'text-green-600' : 'text-red-600'
  const trendIcon = trend === 'up' ? '↑' : '↓'

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm text-secondary-600">{title}</div>
        {icon && <div className="text-secondary-400">{icon}</div>}
      </div>
      <div className="text-3xl font-bold text-secondary-900">{value}</div>
      {change !== undefined && change !== null && (
        <div className={`text-sm font-medium mt-2 ${trendColor}`}>
          {trendIcon} {Math.abs(change)}%
        </div>
      )}
    </div>
  )
}
